import React, { FC, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Dayjs } from 'dayjs';
import { CircularProgress, Typography } from '@mui/material';
import { mdiCreation, mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { TaskItem } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import { CustomTimeSelector, CustomTooltip } from '../../common';
import './TaskTimeWithSuggestion.scss';

interface TaskTimeWithSuggestionProps {
  date: string;
  value: Dayjs | null;
  onChange: (time: Dayjs | null) => void;
  task: Partial<TaskItem>;
  timeFormat: '12h' | '24h';
  disabled?: boolean;
  isPending?: boolean;
  canRequestSuggestion?: boolean;
  suggestedTime?: Dayjs | null;
  suggestionExplanation?: string;
  onFindBestTime?: () => void | Promise<void>;
  onClearSuggestion?: () => void;
  className?: string;
  selectorClassName?: string;
  autocompleteClassName?: string;
  placeholder?: string;
  selectorSize?: 'small' | 'medium';
  open?: boolean;
  setOpen?: (open: boolean) => void;
  defaultTimeInSelector?: Dayjs | null;
  showClearButton?: boolean;
  showConnector?: boolean;
  showFindBestTime?: boolean;
}

const TaskTimeWithSuggestion: FC<TaskTimeWithSuggestionProps> = ({
  date: _date,
  value,
  onChange,
  timeFormat,
  disabled,
  isPending = false,
  canRequestSuggestion = true,
  suggestedTime,
  suggestionExplanation,
  onFindBestTime,
  onClearSuggestion,
  className,
  selectorClassName,
  autocompleteClassName,
  placeholder,
  selectorSize = 'small',
  open,
  setOpen,
  defaultTimeInSelector,
  showClearButton,
  showConnector = true,
  showFindBestTime = true,
}) => {
  const { t } = useTranslation();
  const [localOpen, setLocalOpen] = useState(false);
  const resolvedPlaceholder = placeholder || t('task.quickAdd.time');

  const timePickerOpen = open ?? localOpen;
  const setTimePickerOpen = setOpen ?? setLocalOpen;

  const shouldShowClearButton = showClearButton ?? (!disabled && !!value);

  const handleTimeChange = useCallback(
    (nextTime: Dayjs | null) => {
      if (!nextTime) {
        onClearSuggestion?.();
        onChange(null);
        return;
      }

      if (suggestedTime && !nextTime.isSame(suggestedTime, 'minute')) {
        onClearSuggestion?.();
      }

      onChange(nextTime);
    },
    [onChange, onClearSuggestion, suggestedTime]
  );

  const handleFindBestTime = useCallback(async () => {
    if (disabled || !canRequestSuggestion || isPending) return;
    await onFindBestTime?.();
  }, [canRequestSuggestion, disabled, isPending, onFindBestTime]);

  const showExplanation = useMemo(() => {
    if (!suggestedTime || !value) return false;
    return value.isSame(suggestedTime, 'minute');
  }, [suggestedTime, value]);

  const hasAppliedSuggestion = showExplanation;

  const canShowFindBestTime = showFindBestTime && !value && !disabled;

  return (
    <div className={clsx('task-time-with-suggestion', className)}>
      <div
        className={clsx(
          'task-time-with-suggestion__selector',
          hasAppliedSuggestion && 'task-time-with-suggestion__selector--suggested',
          selectorClassName
        )}
      >
        {value || timePickerOpen ? (
          <CustomTimeSelector
            onChange={handleTimeChange}
            value={value}
            size={selectorSize}
            autocompleteClassName={autocompleteClassName}
            open={timePickerOpen}
            setOpen={setTimePickerOpen}
            className="task-time-with-suggestion__time-selector"
            amPm={timeFormat === '12h'}
            showClearButton={shouldShowClearButton}
            fontWeight="bold"
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            defaultTimeInSelector={defaultTimeInSelector || value}
          />
        ) : (
          <p
            className="task-time-with-suggestion__placeholder"
            onClick={() => setTimePickerOpen(true)}
          >
            {resolvedPlaceholder}
          </p>
        )}

        {showExplanation && suggestionExplanation && (
          <CustomTooltip
            title={
              <Typography variant="subtitle2" color="black">
                {suggestionExplanation}
              </Typography>
            }
          >
            <Icon
              path={mdiInformationOutline}
              size="14px"
              className="task-time-with-suggestion__info-icon"
            />
          </CustomTooltip>
        )}
      </div>

      {canShowFindBestTime && (
        <>
          {showConnector && <div className="task-time-with-suggestion__connector" />}
          <button
            type="button"
            className="task-time-with-suggestion__find-btn"
            onClick={handleFindBestTime}
            disabled={isPending || !canRequestSuggestion}
          >
            <Icon
              path={mdiCreation}
              size="14px"
              className="task-time-with-suggestion__sparkle"
            />
            {isPending ? (
              <CircularProgress size={14} className="task-time-with-suggestion__spinner" />
            ) : (
              <span className="task-time-with-suggestion__text">
                {t('task.card.smartSchedule')}
              </span>
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default TaskTimeWithSuggestion;
